import type { ReactNode } from 'react';

import { maxBreakpoint, minBreakpoint } from 'imports/ui/layouts/breakpoints';

import logoSvgInverted from '/assets/logomark-inverted.svg';
import logoSvg from '/assets/logomark.svg';
import { Icon } from '/imports/ui/foundation/Icons';
import { important, isLight, styled, useTheme } from '/imports/ui/foundation/Theme';

export interface TopNavEditorProps {
  name: string;
  closeLink: string;
  title?: ReactNode;
  actions?: ReactNode;
  utils?: ReactNode;
}

export const TopNavEditor = ({ name, closeLink, title, actions, utils }: TopNavEditorProps) => {
  const { colorScheme } = useTheme();
  const logoPath = isLight(colorScheme) ? logoSvg : logoSvgInverted;

  return (
    <Container hasTitle={title != null}>
      <Logo>
        <a href={closeLink}>
          <img src={logoPath} data-logo alt="Code Expert" />
          <Icon name="reply" size="2x" />
        </a>
      </Logo>
      <Name>{name}</Name>
      {title != null && <Title data-testid="topnav-title">{title}</Title>}
      {actions != null && <Actions>{actions}</Actions>}
      {utils != null && <Utils hasActions={actions != null}>{utils}</Utils>}
    </Container>
  );
};

// -------------------------------------------------------------------------------------------------

const GAP_WIDTH = 16;
const NAME_WIDTH = 164;

const Container = styled('div', () => ({
  display: 'grid',
  minHeight: 64,

  gridTemplateColumns: `20px 52px ${GAP_WIDTH}px ${NAME_WIDTH}px 1fr min-content 20px min-content 20px`,
  gridTemplateAreas: '"logo logo . name title actions . utils ."',

  [maxBreakpoint('desktop')]: {
    gridTemplateColumns: `20px 52px ${GAP_WIDTH}px 1fr min-content 20px min-content 20px`,
    gridTemplateAreas: `
      "logo logo . name  actions . utils ."
      "logo logo . title title   . utils ."
    `,
  },
  variants: {
    hasTitle: {
      false: {
        [maxBreakpoint('desktop')]: {
          gridTemplateAreas: `
            "logo logo . name  actions . utils ."
            "logo logo . name  title   . utils ."
          `,
        },
      },
    },
  },

  [maxBreakpoint('tablet')]: {
    gridTemplateColumns: `20px 52px ${GAP_WIDTH}px 1fr ${GAP_WIDTH}px min-content min-content 20px`,
    gridTemplateAreas: `
      "logo logo  .     name  name name    utils   ."
      ".    title title title .    actions actions ."
    `,
  },

  [maxBreakpoint('mobile')]: {
    gridTemplateColumns: `20px 52px ${GAP_WIDTH}px 1fr min-content 20px`,
    gridTemplateAreas: `
      "logo logo    .       name    utils   ."
      ".    title   title   title   title   ."
      ".    actions actions actions actions ."
    `,
  },
}));

const Logo = styled('div', ({ tokens }) => ({
  gridArea: 'logo',
  display: 'grid',

  '& > a': {
    contain: 'layout',
    display: 'grid',
    placeContent: 'center',
    minHeight: 50,
    color: tokens.colorIcon,

    '[data-logo]': {
      width: 50,
    },

    '[data-icon]': {
      display: 'none',
    },

    '&:hover': {
      '[data-logo]': {
        display: 'none',
      },
      '[data-icon]': {
        display: 'block',
      },
    },

    '&::before': {
      content: '""',
      display: 'block',
      position: 'absolute',
      inset: 0,
      marginRight: -(GAP_WIDTH + NAME_WIDTH),
      [maxBreakpoint('desktop')]: {
        // Hide on smaller viewports, because their "name" slot is dynamically sized
        display: 'none',
      },
    },

    '&::after': {
      content: '""',
      display: 'block',
      position: 'absolute',
      top: '10%',
      right: 0,
      width: 1,
      height: '80%',
      background: tokens.colorBorder,
    },
  },
}));

const Name = styled('div', ({ tokens }) => ({
  gridArea: 'name',
  display: 'grid',
  alignItems: 'center',
  color: tokens.colorTextSecondary,
  fontSize: tokens.fontSizeHeading3,
  lineHeight: 1.1,
}));

const Title = styled('div', ({ tokens }) => {
  // These calculations help us keep a consistent baseline
  // that matches that of the Name component.
  const w = 1200; // Max window width at which dynamic calculations have an effect
  const a = tokens.fontSizeLG; // Min font-size
  const b = tokens.fontSizeHeading4; // Max font-size
  const s = (b / w) * 100; // Viewport-based slope in %
  const d = 2; // Baseline correction shift in px

  return {
    gridArea: 'title',
    display: 'grid',
    alignItems: 'center',
    fontWeight: tokens.fontWeightStrong,
    lineHeight: tokens.lineHeightHeading4,
    textWrap: 'balance',
    paddingBlock: tokens.sizeXS,

    // Responsive values
    marginBottom: important(`round(down, calc(clamp(${a}px, ${s}vw, ${b}px) - ${b + d}px), 1px)`),
    fontSize: `round(down, clamp(${a}px, ${s}vw, ${b}px), 1px)`,

    [maxBreakpoint('desktop')]: {
      paddingTop: 0,
      paddingBottom: tokens.sizeMS,
    },
  };
});

const Actions = styled('div', ({ tokens }) => ({
  gridArea: 'actions',
  display: 'grid',
  alignItems: 'center',
  paddingBlock: tokens.sizeXS,

  [maxBreakpoint('tablet')]: {
    alignItems: 'start',
  },

  [maxBreakpoint('mobile')]: {
    justifyContent: 'center',
  },
}));

const Utils = styled('div', ({ tokens }) => ({
  gridArea: 'utils',
  display: 'grid',
  alignItems: 'center',
  position: 'relative',
  paddingInlineStart: tokens.sizeSM,
  paddingBlock: tokens.sizeXS,

  variants: {
    hasActions: {
      true: {
        [minBreakpoint('tablet')]: {
          '&::before': {
            content: '""',
            display: 'block',
            position: 'absolute',
            top: '10%',
            left: 0,
            width: 1,
            height: '80%',
            background: tokens.colorBorder,
          },
        },
      },
    },
  },
}));
